import React, { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Text,
  Switch,
  MessageBar,
  MessageBarBody,
  Select,
  Input,
  Label,
} from '@fluentui/react-components';
import { apiService } from '../services/apiService';
import { getAccessToken, getUserIdFromToken } from '../services/authService';
import {
  FeatureToggles,
  UserPreferences,
  SummaryLength,
  type TaskPaneConfig,
} from '@shared/types/user';
import type { DashboardStatsResponse } from '../services/apiService';
import { FEATURE_NAMES } from '@shared/utils/constants';
import { DEFAULT_TASK_PANE_CONFIG } from '@shared/utils/constants';

const TASK_PANE_POSITIONS: { value: TaskPaneConfig['position']; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];

const SUMMARY_LENGTH_OPTIONS: { value: SummaryLength; label: string }[] = [
  { value: 'short', label: 'Short' },
  { value: 'detailed', label: 'Detailed' },
];

const WIDTH_MIN = 280;
const WIDTH_MAX = 600;
const WIDTH_DEFAULT = DEFAULT_TASK_PANE_CONFIG.width;

interface SettingsPanelProps {
  onPreferencesSaved?: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ onPreferencesSaved }) => {
  const [currentUserId, setCurrentUserId] = useState<string>('current-user-id');
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [statistics, setStatistics] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        const user = getUserIdFromToken(token);
        if (!cancelled) setCurrentUserId(user?.oid ?? 'current-user-id');
      } catch {
        if (!cancelled) setCurrentUserId('current-user-id');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadPreferences(currentUserId);
  }, [currentUserId]);

  useEffect(() => {
    if (preferences?.featureToggles?.dashboard) {
      loadDashboard(currentUserId);
    } else {
      setStatistics(null);
    }
  }, [preferences?.featureToggles?.dashboard, currentUserId]);

  const loadPreferences = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getUserPreferences(userId);
      setPreferences(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async (userId: string) => {
    try {
      const data = await apiService.getDashboardStatistics(userId);
      setStatistics(data);
    } catch {
      setStatistics(null);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await apiService.updateUserPreferences(currentUserId, {
        featureToggles: preferences.featureToggles,
        summaryLength: preferences.summaryLength,
        taskPaneConfig: preferences.taskPaneConfig,
      });
      setSuccess('Preferences saved successfully!');
      onPreferencesSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const updateFeatureToggle = (feature: keyof FeatureToggles, value: boolean) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      featureToggles: {
        ...preferences.featureToggles,
        [feature]: value,
      },
    });
  };

  const updateSummaryLength = (value: SummaryLength) => {
    if (!preferences) return;
    setPreferences({ ...preferences, summaryLength: value });
  };

  const updateTaskPaneConfig = (updates: Partial<TaskPaneConfig>) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      taskPaneConfig: { ...preferences.taskPaneConfig, ...updates },
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner label="Loading settings..." />
      </div>
    );
  }

  if (error && !preferences) {
    return (
      <div>
        <Text>{error}</Text>
        <Button onClick={() => loadPreferences('current-user-id')} style={{ marginTop: '10px' }}>
          Retry
        </Button>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div>
        <Text>Loading preferences...</Text>
      </div>
    );
  }

  const stats = statistics;
  const summaries = stats?.summariesGenerated ?? 0;
  const actions = stats?.actionsExecuted ?? 0;
  const spoofChecks = stats?.spoofChecks ?? 0;
  const toggles = preferences.featureToggles ?? {};
  const summariesOn = toggles[FEATURE_NAMES.SUMMARIES] ?? true;
  const automationOn = toggles[FEATURE_NAMES.AUTOMATION] ?? true;
  const taskPane = preferences.taskPaneConfig ?? DEFAULT_TASK_PANE_CONFIG;
  const widthNum = Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, taskPane.width ?? WIDTH_DEFAULT));

  return (
    <div>
      {success && (
        <MessageBar intent="success" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{success}</MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Dashboard section */}
      <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: '12px' }}>
        Dashboard
      </Text>
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '8px' }}>
          Activity
        </Text>
        <Text size={300}>
          Summaries: {summaries} · Actions: {actions} · Spoof checks: {spoofChecks}
        </Text>
        {(stats?.periodStart || stats?.periodEnd) && (
          <Text size={200} style={{ color: '#666', display: 'block', marginTop: '4px' }}>
            Period: {stats.periodStart ?? '—'} to {stats.periodEnd ?? '—'}
          </Text>
        )}
      </div>

      <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '16px' }}>
        Feature Toggles
      </Text>

      {/* Summary (with sub-settings: Summary Length, Spoof Detection) */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            padding: '8px',
            border: '1px solid #e1dfdd',
            borderRadius: '4px',
          }}
        >
          <Text>Summary</Text>
          <Switch
            checked={summariesOn}
            onChange={(_, data) =>
              updateFeatureToggle(FEATURE_NAMES.SUMMARIES as keyof FeatureToggles, data.checked)
            }
          />
        </div>
        {summariesOn && (
          <div style={{ marginLeft: '16px', marginBottom: '12px' }}>
            <Label size="small" style={{ display: 'block', marginBottom: '4px' }}>
              Summary Length
            </Label>
            <Select
              value={preferences.summaryLength ?? 'short'}
              onChange={(_, data) => updateSummaryLength((data.value as SummaryLength) ?? 'short')}
              style={{ marginBottom: '12px', minWidth: '140px' }}
            >
              {SUMMARY_LENGTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                border: '1px solid #e1dfdd',
                borderRadius: '4px',
              }}
            >
              <Text size={300}>Spoof Detection</Text>
              <Switch
                checked={toggles[FEATURE_NAMES.SPOOF_DETECTION] ?? true}
                onChange={(_, data) =>
                  updateFeatureToggle(FEATURE_NAMES.SPOOF_DETECTION as keyof FeatureToggles, data.checked)
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Links */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            border: '1px solid #e1dfdd',
            borderRadius: '4px',
          }}
        >
          <Text>Links</Text>
          <Switch
            checked={toggles[FEATURE_NAMES.LINKS] ?? true}
            onChange={(_, data) =>
              updateFeatureToggle(FEATURE_NAMES.LINKS as keyof FeatureToggles, data.checked)
            }
          />
        </div>
      </div>

      {/* Automation (with sub-setting: Tasks) */}
      <div style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            padding: '8px',
            border: '1px solid #e1dfdd',
            borderRadius: '4px',
          }}
        >
          <Text>Automation</Text>
          <Switch
            checked={automationOn}
            onChange={(_, data) =>
              updateFeatureToggle(FEATURE_NAMES.AUTOMATION as keyof FeatureToggles, data.checked)
            }
          />
        </div>
        {automationOn && (
          <div style={{ marginLeft: '16px', marginBottom: '12px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                border: '1px solid #e1dfdd',
                borderRadius: '4px',
              }}
            >
              <Text size={300}>Tasks</Text>
              <Switch
                checked={toggles[FEATURE_NAMES.ACTIONS] ?? true}
                onChange={(_, data) =>
                  updateFeatureToggle(FEATURE_NAMES.ACTIONS as keyof FeatureToggles, data.checked)
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Dashboard toggle */}
      <div style={{ marginBottom: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px',
            border: '1px solid #e1dfdd',
            borderRadius: '4px',
          }}
        >
          <Text>Dashboard</Text>
          <Switch
            checked={toggles[FEATURE_NAMES.DASHBOARD] ?? true}
            onChange={(_, data) =>
              updateFeatureToggle(FEATURE_NAMES.DASHBOARD as keyof FeatureToggles, data.checked)
            }
          />
        </div>
      </div>

      {/* Task Pane Configuration */}
      <Text weight="semibold" size={400} style={{ display: 'block', marginBottom: '16px' }}>
        Task Pane Configuration
      </Text>
      <div style={{ marginBottom: '24px' }}>
        <Label size="small" style={{ display: 'block', marginBottom: '4px' }}>
          Position
        </Label>
        <Select
          value={taskPane.position ?? 'right'}
          onChange={(_, data) =>
            updateTaskPaneConfig({
              position: (data.value as TaskPaneConfig['position']) ?? 'right',
            })
          }
          style={{ marginBottom: '12px', minWidth: '120px' }}
        >
          {TASK_PANE_POSITIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Label size="small" style={{ display: 'block', marginBottom: '4px' }}>
          Width (px)
        </Label>
        <Input
          type="number"
          min={WIDTH_MIN}
          max={WIDTH_MAX}
          value={String(widthNum)}
          onChange={(_, data) => {
            const v = Number(data.value);
            if (!Number.isNaN(v)) updateTaskPaneConfig({ width: Math.min(WIDTH_MAX, Math.max(WIDTH_MIN, v)) });
          }}
          style={{ width: '80px' }}
        />
        <Text size={200} style={{ color: '#666', marginLeft: '8px', display: 'inline' }}>
          Default 350. Applied when supported by the host.
        </Text>
      </div>

      <Button
        appearance="primary"
        onClick={savePreferences}
        disabled={saving}
        style={{ width: '100%' }}
      >
        {saving ? (
          <>
            <Spinner size="tiny" style={{ marginRight: '8px' }} />
            Saving...
          </>
        ) : (
          'Save Preferences'
        )}
      </Button>
    </div>
  );
};

export default SettingsPanel;
